import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TableContainer, Table, TableHead, TableRow, TableCell,
  TableBody, Paper, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, Select, MenuItem,
} from '@mui/material';
import SearchBar from '../SearchBar';
import FilterPanel from '../FilterPanel';

const getStatusColor = (status) => {
  switch (status) {
    case 'estimate': return 'warning';
    case 'pending_approval': return 'info';
    case 'rejected': return 'error';
    case 'active': return 'success';
    default: return 'default';
  }
};

const AdminEstimatesTable = ({
  estimates, searchQuery, onSearch, filters, onFilterChange,
  homeownersList = [], onAssignHomeowner,
}) => {
  const navigate = useNavigate();
  const [selectedSignature, setSelectedSignature] = useState(null);
  const [signatureDialog, setSignatureDialog] = useState(false);

  return (
    <>
      <Box mb={3}>
        <SearchBar placeholder="Search estimates..." onSearch={onSearch} defaultValue={searchQuery} />
      </Box>
      <Box mb={3}>
        <FilterPanel filters={filters} onFilterChange={onFilterChange} />
      </Box>

      {estimates.length === 0 ? (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary">No estimates in the system</Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Estimates will appear here when contractors create them
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell><strong>Project Name</strong></TableCell>
                <TableCell><strong>Contractor</strong></TableCell>
                <TableCell><strong>Homeowner</strong></TableCell>
                {onAssignHomeowner && <TableCell><strong>Assign Homeowner</strong></TableCell>}
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Total Cost</strong></TableCell>
                <TableCell><strong>Submitted</strong></TableCell>
                <TableCell><strong>Approved</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {estimates.map((estimate) => (
                <TableRow key={estimate.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{estimate.name}</Typography>
                  </TableCell>
                  <TableCell>{estimate.contractor_first_name} {estimate.contractor_last_name}</TableCell>
                  <TableCell>
                    {estimate.homeowner_first_name
                      ? `${estimate.homeowner_first_name} ${estimate.homeowner_last_name}`
                      : <Chip label="Not Assigned" size="small" color="warning" />}
                  </TableCell>
                  {onAssignHomeowner && (
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                          value={estimate.homeowner_id || ''}
                          onChange={(e) => onAssignHomeowner(estimate.id, e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="" disabled>
                            {homeownersList.length === 0 ? 'No homeowners available' : 'Select homeowner...'}
                          </MenuItem>
                          {homeownersList.map((h) => (
                            <MenuItem key={h.id} value={h.id}>
                              {h.first_name} {h.last_name} ({h.email})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  )}
                  <TableCell>
                    <Chip label={estimate.status} size="small" color={getStatusColor(estimate.status)} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      ${parseFloat(estimate.total_cost || 0).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {estimate.submitted_at ? (
                      <>
                        <Typography variant="body2">{new Date(estimate.submitted_at).toLocaleDateString()}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(estimate.submitted_at).toLocaleTimeString()}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">Not submitted</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {estimate.approved_at ? (
                      <>
                        <Typography variant="body2">{new Date(estimate.approved_at).toLocaleDateString()}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(estimate.approved_at).toLocaleTimeString()}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">Not approved</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Button variant="outlined" size="small" onClick={() => navigate(`/estimate-review/${estimate.id}`)}>
                        View Estimate
                      </Button>
                      {estimate.approval_signature && (
                        <Button
                          variant="contained"
                          color="info"
                          size="small"
                          onClick={() => { setSelectedSignature(estimate.approval_signature); setSignatureDialog(true); }}
                        >
                          View Signature
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={signatureDialog} onClose={() => setSignatureDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Homeowner Approval Signature</DialogTitle>
        <DialogContent>
          {selectedSignature ? (
            <Box display="flex" justifyContent="center" p={2}>
              <img
                src={selectedSignature}
                alt="Approval Signature"
                style={{ maxWidth: '100%', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </Box>
          ) : (
            <Typography color="text.secondary">No signature available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignatureDialog(false)} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminEstimatesTable;
